import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import API from '../services/api';
import SignOffStatus from './SignOffStatus';
import './IncidentReview.css';

const IncidentReview = () => {
  const { incidentId } = useParams();
  const { user } = useAuth();
  const isManager = user?.role?.toLowerCase() === 'manager';
  const isSupervisor = user?.role?.toLowerCase() === 'field_supervisor';
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  console.log('User object:', user);
  console.log('User role:', user?.role);

  useEffect(() => {
    const fetchIncident = async () => {
      try {
        const response = await API.get(`/api/incidents/${incidentId}/review`);
        setIncident(response.data.incident);
      } catch (err) {
        console.error('Error fetching incident:', err);
        setError('Failed to load incident details.');
      } finally {
        setLoading(false);
      }
    };

    fetchIncident();
  }, [incidentId]);

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      await API.post(`/api/incidents/${incidentId}/${action}`, {
        role: user.role,
        feedback,
      });
      alert(`Investigation ${action}ed successfully.`);
    } catch (err) {
      console.error(`Error ${action}ing investigation:`, err);
      alert(`Failed to ${action} investigation.`);
    } finally {
      setActionLoading(false);
    }
  };

  if (!user) {
    return <div>Loading user data...</div>;
  }

  if (loading) {
    return <div>Loading incident details...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="incident-review">
      <h2>Incident Review</h2>
      <p><strong>Incident ID:</strong> {incidentId}</p>
      <p><strong>Title:</strong> {incident.title}</p>
      <p><strong>Date:</strong> {new Date(incident.date).toLocaleDateString()}</p>
      <p><strong>Department:</strong> {incident.primary_department}</p>
      <p><strong>Incident Type:</strong> {incident.type}</p>
      <p><strong>Description:</strong> {incident.description}</p>
      <p><strong>Injured/Involved Worker:</strong> {incident.injured_worker || 'N/A'}</p>
      <p><strong>Witnesses:</strong> {incident.witnesses?.join(', ') || 'None'}</p>

      {/* Attachments */}
      {incident.attachments && incident.attachments.length > 0 && (
        <div>
          <h3>Attachments</h3>
          <ul>
            {incident.attachments.map((attachment, index) => (
              <li key={index}>
                <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                  {attachment.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Investigation Details */}
      <h3>Investigation Details</h3>
      <p><strong>Unsafe Acts:</strong> {incident.unsafe_acts || 'N/A'}</p>
      <p><strong>Unsafe Conditions:</strong> {incident.unsafe_conditions || 'N/A'}</p>
      <p><strong>Personal Factors:</strong> {incident.personal_factors || 'N/A'}</p>
      <p><strong>Job Factors:</strong> {incident.job_factors || 'N/A'}</p>
      <p><strong>Root Causes:</strong> {incident.root_causes || 'N/A'}</p>
      <p><strong>Immediate Actions:</strong> {incident.immediate_actions || 'N/A'}</p>
      <p><strong>Corrective Actions:</strong></p>
      <ul>
        {incident.corrective_actions?.map((action, index) => (
          <li key={index}>
            {action.description} (Assigned to: {action.assignee}, Target Date: {new Date(action.target_date).toLocaleDateString()})
          </li>
        )) || <li>None</li>}
      </ul>

      {/* Investigation Findings Form */}
      {isSupervisor && (
        <div className="investigation-findings">
          <h3>Update Investigation Findings</h3>
          <textarea
            placeholder="Unsafe Acts"
            value={incident.unsafe_acts || ''}
            onChange={(e) => setIncident({ ...incident, unsafe_acts: e.target.value })}
          />
          <textarea
            placeholder="Unsafe Conditions"
            value={incident.unsafe_conditions || ''}
            onChange={(e) => setIncident({ ...incident, unsafe_conditions: e.target.value })}
          />
          <textarea
            placeholder="Root Causes"
            value={incident.root_causes || ''}
            onChange={(e) => setIncident({ ...incident, root_causes: e.target.value })}
          />
          <button
            onClick={async () => {
              try {
                await API.post(`/api/incidents/${incidentId}/update-findings`, {
                  unsafeActs: incident.unsafe_acts,
                  unsafeConditions: incident.unsafe_conditions,
                  rootCauses: incident.root_causes,
                });
                alert('Investigation findings updated successfully.');
              } catch (err) {
                console.error('Error updating findings:', err);
                alert('Failed to update findings.');
              }
            }}
          >
            Save Findings
          </button>
        </div>
      )}

      {/* Sign-Off Status */}
      <SignOffStatus incidentId={incidentId} />

      {/* Manager/Supervisor Actions */}
      {(isManager || isSupervisor) && (
        <div className="review-actions">
          <textarea
            placeholder="Add feedback (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <button onClick={() => handleAction('approve')} disabled={actionLoading}>
            Approve
          </button>
          <button onClick={() => handleAction('reject')} disabled={actionLoading}>
            Reject
          </button>
        </div>
      )}
    </div>
  );
};

export default IncidentReview;